import { useRecoilState } from "recoil";
import { HelpCentreContent, HelpCentreTimestamp } from "@interfaces/Interfaces";
import { helpCentreContentAtom, helpCentreTimestampAtom } from "@state/State";
import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

export interface UseHelpCentreData {
  helpCentreContent: HelpCentreContent[];
  whatsNewSection: HelpCentreContent | null;
}

const fetchHelpCentreTimestamp = async (): Promise<HelpCentreTimestamp> => {
  const response: Response = await fetch(
    "https://parafolio-config.s3.eu-west-2.amazonaws.com/help-centre/timestamp.json",
    {
      headers: {
        "Cache-Control": "no-cache",
      },
    }
  );

  const timestamp = await response.json();
  return timestamp;
};

const fetchHelpCentreContent = async (): Promise<HelpCentreContent[]> => {
  const response: Response = await fetch(
    "https://parafolio-config.s3.eu-west-2.amazonaws.com/help-centre/helpCentre.json",
    {
      headers: {
        "Cache-Control": "no-cache",
      },
    }
  );

  const content = await response.json();
  return content;
};

export const useHelpCentre = (loadData?: boolean): UseHelpCentreData => {
  const [helpCentreTimestamp, setHelpCentreTimestamp] = useRecoilState<HelpCentreTimestamp>(helpCentreTimestampAtom);
  const [helpCentreContent, setHelpCentreContent] = useRecoilState<HelpCentreContent[]>(helpCentreContentAtom);

  const { data: fetchedTimestamp } = useQuery({
    queryKey: ["help-centre-timestamp"],
    queryFn: fetchHelpCentreTimestamp,
    enabled: Boolean(loadData),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

  const shouldFetchContent = useMemo(() => {
    if (!fetchedTimestamp?.lastUpdatedTime) {
      return false;
    }
    return fetchedTimestamp.lastUpdatedTime > (helpCentreTimestamp.lastUpdatedTime || 0);
  }, [fetchedTimestamp, helpCentreTimestamp.lastUpdatedTime]);

  const { data: fetchedContent } = useQuery({
    queryKey: ["help-centre-content"],
    queryFn: fetchHelpCentreContent,
    enabled: Boolean(loadData && shouldFetchContent),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (fetchedTimestamp) {
      setHelpCentreTimestamp(fetchedTimestamp);
    }
  }, [fetchedTimestamp, setHelpCentreTimestamp]);

  useEffect(() => {
    if (fetchedContent) {
      setHelpCentreContent(fetchedContent);
    }
  }, [fetchedContent, setHelpCentreContent]);

  const whatsNewSection = helpCentreContent.find((section) => section.Section.toLowerCase() === "what's new") ?? null;

  return {
    helpCentreContent,
    whatsNewSection,
  };
};
